const palette = {
  primary: "#1d1d1f",
  primaryHover: "#333333",
  primaryActive: "#000000",
  page: "#f5f5f7",
  container: "#ffffff",
  secondaryBg: "#fafafa",
  fill: "#f7f7f8",
  fillHover: "#f1f1f3",
  border: "#e5e5e7",
  borderLight: "#f0f0f0",
  text: "#1d1d1f",
  textSecondary: "#6e6e73",
  textTertiary: "#86868b",
  placeholder: "#a1a1a6",
  disabled: "#b8b8bd",
  success: "#166534",
  successBg: "#dcfce7",
  danger: "#dc2626",
  dangerBg: "#fee2e2",
  warning: "#92400e",
  warningBg: "#fef3c7",
  shadow: "rgba(0, 0, 0, 0.06)"
};

const fontFamily =
  '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const focuslessControl = {
  activeBorderColor: palette.primary,
  hoverBorderColor: palette.primary,
  activeShadow: "none",
  controlOutlineWidth: 0,
  controlOutline: "transparent"
};

// Antdv Next theme config. Keep cssVar enabled so @antdv-next/tailwind can read
// the same --ant-* variables, and keep hashed disabled for existing global styles.
export const antdTheme = {
  cssVar: {
    prefix: "ant",
    key: "ndb-theme"
  },
  hashed: false,
  zeroRuntime: false,
  inherit: true,
  token: {
    colorPrimary: palette.primary,
    colorPrimaryHover: palette.primaryHover,
    colorPrimaryActive: palette.primaryActive,
    colorInfo: palette.primary,
    colorSuccess: palette.success,
    colorSuccessBg: palette.successBg,
    colorWarning: palette.warning,
    colorWarningBg: palette.warningBg,
    colorError: palette.danger,
    colorErrorBg: palette.dangerBg,
    colorLink: palette.primary,
    colorLinkHover: palette.primaryHover,
    colorLinkActive: palette.primaryActive,
    colorTextBase: palette.text,
    colorBgBase: palette.container,
    colorText: palette.text,
    colorTextSecondary: palette.textSecondary,
    colorTextTertiary: palette.textTertiary,
    colorTextDescription: palette.textSecondary,
    colorTextPlaceholder: palette.placeholder,
    colorTextDisabled: palette.disabled,
    colorBorder: palette.border,
    colorBorderSecondary: palette.borderLight,
    colorSplit: palette.borderLight,
    colorBgLayout: palette.page,
    colorBgContainer: palette.container,
    colorBgElevated: palette.container,
    colorBgContainerDisabled: palette.secondaryBg,
    colorFillAlter: palette.secondaryBg,
    colorFillContent: palette.fill,
    colorFillContentHover: palette.fillHover,
    controlItemBgHover: palette.page,
    controlItemBgActive: palette.secondaryBg,
    controlItemBgActiveHover: palette.secondaryBg,
    controlOutline: "transparent",
    controlOutlineWidth: 0,
    fontFamily,
    fontSize: 14,
    fontWeightStrong: 600,
    borderRadius: 8,
    borderRadiusSM: 6,
    borderRadiusLG: 12,
    borderRadiusXS: 4,
    controlHeight: 40,
    controlHeightSM: 32,
    controlHeightLG: 44,
    controlPaddingHorizontal: 16,
    boxShadow: `0 2px 8px ${palette.shadow}`,
    boxShadowSecondary: `0 4px 24px ${palette.shadow}`,
    motion: true
  },
  components: {
    Alert: {
      colorInfo: "#17a2b8",
      colorInfoBg: "#edf8f9",
      colorInfoBorder: "#c7e9ed"
    },
    Button: {
      algorithm: true,
      borderRadius: 24,
      borderRadiusSM: 24,
      controlHeight: 40,
      controlHeightSM: 32,
      controlHeightLG: 44,
      paddingInline: 24,
      paddingInlineLG: 28,
      paddingInlineSM: 16,
      fontWeight: 600,
      // contentFontSize: 14,
      // contentFontSizeLG: 14,
      // contentFontSizeSM: 14,
      primaryShadow: "none",
      defaultShadow: "none",
      dangerShadow: "none",
      colorPrimary: palette.primary,
      colorPrimaryHover: palette.primaryHover,
      colorPrimaryActive: palette.primaryActive,
      defaultHoverColor: palette.primary,
      defaultHoverBorderColor: palette.primary,
      defaultActiveColor: palette.primary,
      defaultActiveBorderColor: palette.primary
    },
    Input: {
      algorithm: true,
      borderRadius: 8,
      controlHeight: 40,
      controlHeightLG: 44,
      paddingInline: 12,
      paddingInlineLG: 14,
      hoverBg: palette.container,
      activeBg: palette.container,
      ...focuslessControl
    },
    InputNumber: {
      algorithm: true,
      borderRadius: 8,
      controlHeight: 40,
      ...focuslessControl
    },
    Checkbox: {
      borderRadiusSM: 2,
      controlInteractiveSize: 16
    },
    Pagination: {
      borderRadiusSM: 2,
      borderRadius: 6
    },
    // Select: {
    //   algorithm: true,
    //   borderRadius: 8,
    //   controlHeight: 40,
    //   controlHeightLG: 44,
    //   colorBgElevated: palette.page,
    //   colorText: palette.text,
    //   optionSelectedColor: "#ffffff",
    //   optionSelectedFontWeight: 500,
    //   optionSelectedBg: palette.primary,
    //   optionActiveBg: palette.page,
    //   optionHeight: 36,
    //   optionPadding: "8px 12px",
    //   controlItemBgHover: palette.page,
    //   controlItemBgActive: palette.page,
    //   activeOutlineColor: "transparent",
    //   ...focuslessControl
    // },
    Radio: {
      algorithm: true,
      buttonBg: palette.container,
      buttonCheckedBg: palette.primary,
      buttonColor: palette.text,
      buttonSolidCheckedBg: palette.primary,
      buttonSolidCheckedHoverBg: palette.primaryHover,
      buttonSolidCheckedActiveBg: palette.primaryActive
    },
    Card: {
      algorithm: true,
      borderRadiusLG: 20,
      colorBorderSecondary: palette.borderLight,
      headerBg: "transparent",
      headerFontSize: 14,
      headerFontSizeSM: 14,
      bodyPadding: 22,
      bodyPaddingSM: 16,
      headerPadding: 22,
      headerPaddingSM: 16,
      extraColor: palette.textSecondary
    },
    Table: {
      algorithm: true,
      headerBg: palette.container,
      headerColor: palette.textSecondary,
      headerSplitColor: palette.borderLight,
      rowHoverBg: palette.page,
      borderColor: palette.borderLight,
      cellPaddingBlock: 12,
      cellPaddingInline: 16,
      cellFontSize: 14,
      rowSelectedBg: palette.fillHover,
      rowSelectedHoverBg: palette.fillHover,
      headerSortHoverBg: palette.secondaryBg,
      headerSortActiveBg: palette.secondaryBg
    },
    Modal: {
      algorithm: true,
      borderRadiusLG: 20,
      titleFontSize: 18,
      titleColor: palette.text,
      contentBg: palette.container,
      headerBg: palette.container,
      footerBg: palette.container,
      bodyPadding: 0
    },
    Tag: {
      algorithm: true,
      borderRadiusSM: 4,
      defaultBg: "#f5f5f5",
      defaultColor: palette.textSecondary,
      solidTextColor: "#ffffff"
    },
    Dropdown: {
      algorithm: true,
      borderRadiusLG: 16,
      colorBgElevated: palette.page,
      controlItemBgHover: palette.page,
      controlItemBgActive: palette.secondaryBg,
      paddingBlock: 8
    },
    Tabs: {
      algorithm: true,
      inkBarColor: palette.primary,
      itemSelectedColor: palette.primary,
      itemHoverColor: palette.primaryHover,
      itemActiveColor: palette.primary
    },
    // Pagination: {
    //   algorithm: true,
    //   itemActiveBg: palette.primary,
    //   colorPrimary: palette.primary,
    //   colorPrimaryHover: palette.primaryHover,
    //   colorPrimaryActive: palette.primaryActive
    // },
    Tooltip: {
      algorithm: true,
      borderRadius: 12,
      maxWidth: 280
    }
  }
};

export default antdTheme;
